// Pure introspection over a definition + value tree: the flat "what fields
// exist, are they shown, and what's their value" view that FieldNode computes
// per-node but never collects. Mirrors FieldNode's level-building and
// repeatable-row expansion exactly (see DynamicFormRenderer.tsx rootLevels and
// FieldNode's GroupNode/RepeatableGroupNode), so visibility resolves the same
// way the rendered tree does. Pure — no React, no store; feed it
// store.getSnapshot() at call time.

import {
  type DynamicFormDefinition,
  type DynamicFormField,
  type DynamicFormValue,
  isGroupField,
  isRepeatableGroup,
} from './types'
import { type ResolutionLevel, isFieldVisible } from './visibility'
import { asObject } from './value'
import { captionText } from './caption'

export interface FieldState {
  /** Dotted/bracketed path, e.g. "address.zip", "contacts[0].email". */
  path: string
  key: string
  type: string
  label?: string
  required: boolean
  /**
   * Whether the field would render: its visibility rules pass, it is not the
   * `hidden` attribute, and no ancestor is itself hidden. Matches FieldNode's
   * null-render checks.
   */
  visible: boolean
  /** In-memory field definition ref — NOT JSON-serializable. */
  field: DynamicFormField
  /** Current value at this path (raw; group/row values may carry ROW_KEY). */
  value: unknown
}

/**
 * Depth-first flat view of every keyed field, root-first. Groups appear as
 * their own entry (value = the group object, or the rows array for a repeatable
 * group) and then expand: plain groups descend one level, repeatable groups
 * expand each row into `foo[i].child` paths (each row its own resolution
 * level). A field under a hidden ancestor is `visible: false` even when its own
 * rules pass — parent visibility propagates down.
 */
export function collectFieldStates(
  definition: DynamicFormDefinition,
  value: DynamicFormValue,
): FieldState[] {
  const states: FieldState[] = []
  const root: ResolutionLevel = { fields: definition.fields, value }
  collect(definition.fields, value, [root], '', true, states)
  return states
}

function collect(
  fields: DynamicFormField[],
  valueObj: Record<string, unknown> | undefined,
  levels: ResolutionLevel[],
  pathPrefix: string,
  parentVisible: boolean,
  states: FieldState[],
): void {
  for (const field of fields) {
    if (!field.key) continue
    const path = pathPrefix ? `${pathPrefix}.${field.key}` : field.key
    const fieldValue = valueObj?.[field.key]
    const visible = parentVisible && field.hidden !== true && isFieldVisible(field, levels)

    states.push({
      path,
      key: field.key,
      type: field.type,
      // Plain text even for html_labels fields: FieldState.label feeds host and
      // agent tooling (FormWalkerToolCard, prefill catalogs, message strings).
      label: field.label == null ? field.label : captionText(field, field.label),
      required: field.required ?? false,
      visible,
      field,
      value: fieldValue,
    })

    if (!isGroupField(field)) continue
    const children = field.children ?? []

    if (isRepeatableGroup(field)) {
      const rows = Array.isArray(fieldValue) ? fieldValue : []
      rows.forEach((row, i) => {
        const rowObj = asObject(row)
        const rowLevels: ResolutionLevel[] = [...levels, { fields: children, value: rowObj }]
        collect(children, rowObj, rowLevels, `${path}[${i}]`, visible, states)
      })
    } else {
      const childObj = asObject(fieldValue)
      const childLevels: ResolutionLevel[] = [...levels, { fields: children, value: childObj }]
      collect(children, childObj, childLevels, path, visible, states)
    }
  }
}
